import React, { useCallback, useMemo } from "react";

import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import {
  IFilterComponentProps,
  IFilterInstance,
} from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";
import { SelectField, SelectFieldComponent } from "@mapmycustomers/ui";

import defaultSelectOptionMapper from "@app/util/mappers/defaultSelectOptionMapper";

export const FUNNEL_FILTER_OPERATORS = [
  FilterOperator.EQUALS,
  FilterOperator.IN_ANY,
  FilterOperator.NONE_OF,
];

const doesSupportValue = (value: any, operator: FilterOperator) =>
  (Array.isArray(value) &&
    value.every((item) => typeof item === "number") &&
    (operator === FilterOperator.IN_ANY || operator === FilterOperator.NONE_OF)) ||
  (typeof value === "number" && operator === FilterOperator.EQUALS);

interface FunnelFilterProps extends IFilterComponentProps {}

const FunnelFilter: IFilterInstance = {
  doesSupportOption: (option: FilterOption) => option === FilterOption.AVAILABLE_FUNNELS,
  doesSupportValue,
  getComponent:
    (): ((props: FunnelFilterProps) => JSX.Element) =>
    ({ className, disabled, focus, onChange, options, placeholder, value }: FunnelFilterProps) => {
      const selectOptions = useMemo(
        () => ((options?.availableFunnels ?? []) as Funnel[]).map(defaultSelectOptionMapper),
        [options?.availableFunnels]
      );

      const setRef = useCallback(
        (ref: null | SelectFieldComponent) => {
          if (focus && ref) {
            ref.focus();
          }
        },
        [focus]
      );

      return value.operator === FilterOperator.EQUALS ? (
        <SelectField<Funnel["id"]>
          className={className}
          disabled={disabled}
          filterOption={defaultFilterOption}
          onChange={(funnelId: Funnel["id"]) => onChange?.({ ...value, value: funnelId })}
          options={selectOptions}
          placeholder={placeholder}
          ref={setRef}
          size="middle"
          value={typeof value.value === "number" ? value.value : undefined}
        />
      ) : (
        <SelectField<Funnel["id"][]>
          className={className}
          disabled={disabled}
          filterOption={defaultFilterOption}
          maxTagCount="responsive"
          mode="multiple"
          onChange={(funnelIds: Funnel["id"][]) => onChange?.({ ...value, value: funnelIds })}
          options={selectOptions}
          placeholder={placeholder}
          ref={setRef}
          size="middle"
          value={Array.isArray(value.value) ? value.value : undefined}
        />
      );
    },
};

export default FunnelFilter;
